import React, { useState, useEffect, useMemo } from 'react'

import { Loader } from '@reactiveonline/frontend_shared_components'
import Step from './reservation_widget/Step'
import { getTranslatableField } from '../../helpers/utils'
import usePrevious from './hooks/usePrevious'

import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import moment from 'moment'
import { DayPickerSingleDateController } from 'react-dates'

export default function ReservationWidget({
  appProps, selectedService, currentReservation, actionText, disableFormStyles,
  selectedStore, reschedule
}) {
  const [loading, setLoading] = useState(false)
  const [availableServices, setAvailableServices] = useState([])
  const [availableEmployees, setAvailableEmployees] = useState([])
  const [availableTimeSlots, setAvailableTimeSlots] = useState([])
  const [reservation, setReservation] = useState(initializeReservation)

  const { stores, translations, currentLocale } = appProps

  const steps = useMemo( () => {
    let reservationSteps = []

    if (!selectedStore && stores.length > 1) {
      reservationSteps.push('store')
    }

    if (!selectedService) {
      reservationSteps.push('service')
    }

    reservationSteps = [...reservationSteps, 'employee', 'timeSlot']

    return reservationSteps
  }, [])

  const [currentStep, setCurrentStep] = useState(initializeStep)

  const previousReservation = usePrevious(reservation)
  const previousCurrentStep = usePrevious(currentStep)

  const storeValue = reservation.storeId && stores.find( store => store.id === reservation.storeId )?.title
  const serviceValue = reservation.service && getTranslatableField(reservation.service, 'title', currentLocale, false)
  const employeeValue = reservation.employeeId && (
    reservation.employeeId === 'anyone' ? translations.reservations.anyone : availableEmployees.find( employee => employee.id === reservation.employeeId )?.fullName
  )

  useEffect( () => {
    moment.locale(currentLocale)
  }, [])

  useEffect( () => {
    if (reservation.storeId && !selectedService) {
      const store = stores.find( store => store.id === reservation.storeId )
      setAvailableServices(store.services)
    }
  }, [reservation.storeId])

  useEffect( () => {
    if (checkInput()) {
      const resDate = reservation.date.toDate()

      setLoading(true)
      let fd = new FormData()
      fd.append('date', moment(resDate).format())
      fd.append('store_id', reservation.storeId)
      fd.append('service_id', reservation.service.id)
      fd.append('employee_id', reservation.employeeId)

      if (reschedule) {
        fd.append('rescheduled_reservation_id', currentReservation.id)
      }

      Rails.ajax({
        type: 'POST',
        url: appProps.getAvailableTimeSlotsPath,
        dataType: 'json',
        data: fd,
        success: res => {
          setAvailableTimeSlots(res.availableTimeSlots)
          if (!Object.keys(res.availableTimeSlots).includes(reservation.timeSlot)){
            setReservation(prev => ({ ...prev, timeSlot: null }) )
          }
        },
        complete: res => {
          setLoading(false)
        }
      })
    }
  }, [reservation.service, reservation.storeId, reservation.employeeId, reservation.date])

  useEffect( () => {
    if (reservation.service && reservation.storeId) {
      setLoading(true)
      let fd = new FormData()
      fd.append('store_id', reservation.storeId)
      fd.append('service_id', reservation.service.id)

      Rails.ajax({
        type: 'POST',
        url: appProps.getAvailableEmployeesPath,
        dataType: 'json',
        data: fd,
        success: res => {
          setAvailableEmployees(res.available_employees)
          if (reservation.employeeId && !res.available_employees.find( employee => employee.id === reservation.employeeId )) {
            setReservation(prev => ({ ...prev, employeeId: null }))
          }
        },
        complete: res => {
          setLoading(false)
        }
      })
    }
  }, [reservation.service, reservation.storeId])

  useEffect( () => {
    if (!reservation.employeeId && previousReservation?.employeeId && currentStep === 'timeSlot') {
      setCurrentStep(steps[0])
    }
  }, [currentStep, reservation, steps])

  function initializeReservation() {
    moment.locale(currentLocale)

    const initialDate = currentReservation ? moment(new Date(currentReservation.reservation_time_from)) : moment(new Date)
    const initialStoreId = currentReservation && stores.find( store => store.id === currentReservation.storeId ) ? currentReservation.storeId : null
    const initialService = selectedService || currentReservation?.service
    const initialEmployeeId = currentReservation ? currentReservation.employeeId : null
    const initialTimeSlot = currentReservation ? currentReservation.time_from : null

    return {
      date: initialDate,
      storeId: initialStoreId,
      service: initialService,
      employeeId: initialEmployeeId,
      timeSlot: initialTimeSlot
    }
  }

  function initializeStep() {
    if (reservation.storeId && reservation.service && reservation.employeeId) {
      return steps[steps.length - 1]
    }

    return steps[0]
  }

  function handleSelectStore(storeId) {
    if (storeId !== reservation.storeId) {
      let updatedParams = {
        storeId: storeId,
        employeeId: null,
        timeSlot: null
      }

      if (!selectedService) {
        const store = stores.find( store => store.id === storeId )
        const existingService = reservation.service && store.services.find( service => service.id === reservation.service.id )

        updatedParams.service = existingService
      }

      setReservation( prevState => ({ ...prevState, ...updatedParams }) )
    }

    handleNextStep()
  }

  function handleSelectValue(key, value) {
    setReservation( prevState => ({ ...prevState, [key]: value }) )
    handleNextStep()
  }

  function handleNextStep() {
    const currentStepIndex = steps.findIndex( step => step === currentStep )
    if (currentStepIndex < (steps.length - 1)) {
      setCurrentStep(steps[currentStepIndex + 1])
    }
  }

  function checkInput() {
    return (reservation.date && reservation.employeeId && reservation.service && reservation.storeId)
  }

  function handleCreateReservation() {
    setLoading(true)

    const [hours, minutes] = reservation.timeSlot.split(':')
    const resDate = reservation.date.toDate()
    resDate.setHours(hours, minutes, 0)

    const selectedTimeSlotEmployees = availableTimeSlots[reservation.timeSlot]

    let fd = new FormData()
    fd.append('reservation[store_id]', reservation.storeId)
    fd.append('service_id', reservation.service.id)
    fd.append('available_employees', JSON.stringify(selectedTimeSlotEmployees))
    fd.append('reservation[interval]', reservation.service.intervalTime + reservation.service.processingTime)
    fd.append('reservation[duration]', reservation.service.intervalTime)
    fd.append('reservation[people]', 1)
    fd.append('reservation[reserved_at]', moment(resDate).format())

    if (reschedule) {
      fd.append('reschedule', true)
      fd.append('reservation_id', currentReservation.id)

    } else {
      fd.append('reservation[status]', 'cart')
    }

    Rails.ajax({
      type: currentReservation ? 'PATCH' : 'POST',
      url: appProps.reservationPath,
      dataType: 'json',
      data: fd,
      success: res => {
        window.location.href = res.redirect_url
      }
    })
  }

  return (
    <>
      <div className={ `reservation-widget flex-box flex-wrap ${ !disableFormStyles ? 'form box-shadow' : '' }` }>
        <div>
          <div className='option-header date flex-box items-center'>
            <i className='option-icon fal fa-calendar'/>

            <div className='option-title'>
              { translations.reservations.date }
            </div>
          </div>

          <DayPickerSingleDateController
            date={ reservation.date }
            onDateChange={ value => setReservation( prevState => ({ ...prevState, date: value ? moment(value) : null }) ) }
            numberOfMonths={ 1 }
            firstDayOfWeek={ 1 }
            isOutsideRange={ date => date.isBefore(moment(new Date()), 'day') }
            noBorder
            hideKeyboardShortcutsPanel
          />
        </div>

        <div className='widget-steps flex-1'>
          { steps.includes('store') &&
            <Step
              title={ translations.reservations.store }
              stepKey='store'
              currentStep={ currentStep }
              setCurrentStep={ setCurrentStep }
              steps={ steps }
              value={ storeValue }
            >
              <div className='options-wrapper flex-box flex-wrap'>
                { stores.map( (store, index) => (
                  <div
                    key={ index }
                    className={ `option-wrapper padded ${ reservation.storeId === store.id ? 'selected' : '' }` }
                    onClick={ () => handleSelectStore(store.id) }
                  >
                    { store.title }
                  </div>
                ))}
              </div>
            </Step>
          }

          { steps.includes('service') &&
            <Step
              title={ translations.services.service }
              stepKey='service'
              currentStep={ currentStep }
              setCurrentStep={ setCurrentStep }
              steps={ steps }
              value={ serviceValue }
            >
              <div className='options-wrapper flex-box flex-wrap'>
                { availableServices.map( (service, index) => (
                  <div
                    key={ index }
                    className={ `option-wrapper flex-box content-center items-center ${ reservation.service?.id === service.id ? 'selected' : '' }` }
                    onClick={ () => handleSelectValue('service', service) }
                  >
                    { service.images.length > 0 &&
                      <img
                        className='option-image'
                        src={ service.images[0].thumbnail }
                      />
                    }

                    <div className='option-content-wrapper flex-box flex-column items-center'>
                      <div className='option-content-title'>
                        { getTranslatableField(service, 'title', currentLocale, false) }
                      </div>

                      <div className='option-content-subtitle'>
                        { service.displayTotalPrice }
                      </div>
                    </div>
                  </div>
                ))}

                { availableServices.length === 0 &&
                  <div className='widget-alert'>
                    { translations.reservations.no_services }
                  </div>
                }
              </div>
            </Step>
          }

          <Step
            title={ translations.reservations.employee }
            stepKey='employee'
            currentStep={ currentStep }
            setCurrentStep={ setCurrentStep }
            steps={ steps }
            value={ employeeValue }
          >
            <div className='options-wrapper flex-box flex-wrap'>
              { [ ...(availableEmployees.length > 1 ? [{ fullName: translations.reservations.anyone, id: 'anyone' }] : []), ...availableEmployees ].map((employee, index) =>
                <div
                  key={ index }
                  className={ `option-wrapper padded flex-box items-center content-center ${ reservation.employeeId === employee.id ? 'selected' : '' }` }
                  onClick={ () => handleSelectValue('employeeId', employee.id) }
                >
                  { employee.id === 'anyone' &&
                    <i className='fal fa-shuffle'/>
                  }

                  <div>
                    { employee.fullName }
                  </div>
                </div>
              )}

              { availableEmployees.length === 0 &&
                <div className='widget-alert'>
                  { translations.reservations.no_employees }
                </div>
              }
            </div>
          </Step>

          <Step
            title={ translations.reservations.time_slot }
            stepKey='timeSlot'
            currentStep={ currentStep }
            setCurrentStep={ setCurrentStep }
            steps={ steps }
            value={ reservation.timeSlot }
            isLast
          >
            { !loading && Object.keys(availableTimeSlots).length === 0 &&
              <div className='widget-alert'>
                { translations.reservations.no_time_slots }
              </div>
            }

            { Object.keys(availableTimeSlots).length > 0 &&
              <div className='options-wrapper flex-box flex-wrap'>
                { Object.keys(availableTimeSlots).map((timeSlot, index) =>
                  <div
                    key={ index }
                    className={ `option-wrapper padded ${ reservation.timeSlot === timeSlot ? 'selected' : '' }` }
                    onClick={ () => handleSelectValue('timeSlot', timeSlot) }
                  >
                    { timeSlot }
                  </div>
                  )
                }
              </div>
            }
          </Step>

          { reservation.timeSlot && currentStep === 'timeSlot' &&
            <div className='flex-box content-center'>
              <button
                type='button'
                className='widget-button'
                onClick={ handleCreateReservation }
              >
                { actionText }
              </button>
            </div>
          }
        </div>
      </div>

      { loading &&
        <Loader
          size='large'
          position='center'
          fullPage
        />
      }
    </>
  )
}
